import { yupResolver } from '@hookform/resolvers/yup';
import { Button, ButtonLevel } from '@vizit/atoms';
import { FormDialog, FormTextField } from '@vizit/forms';
import { Edit03Icon, LockIcon } from '@vizit/icons';
import { TextField } from '@vizit/molecules';
import { colors } from '@vizit/theme';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

type EditANewProductFormSchema = {
  productTitle: string;
  brand?: string;
};

interface EditANewProductDialogProps {
  /**
   * Default values for the form
   */
  defaultValues?: EditANewProductFormSchema & { categoryName?: string };
  /**
   * The text for the trigger button
   *
   * @optional
   * @type {string}
   */
  triggerButtonText?: string;
  /**
   * Callback function to handle the edit new product api call
   */
  onSubmit: (data: EditANewProductFormSchema) => Promise<void>;
  /**
   * Whether the dialog is open (controlled mode)
   *
   * @optional
   * @type {boolean}
   */
  isOpen?: boolean;
  /**
   * Callback fired when the dialog open state changes (controlled mode)
   *
   * @optional
   * @type {(isOpen: boolean) => void}
   */
  onOpenChange?: (isOpen: boolean) => void;
}

function EditANewProductDialog(props: EditANewProductDialogProps) {
  const {
    defaultValues = {
      brand: '',
      productTitle: '',
      categoryName: '',
    },
    triggerButtonText = 'New product',
    onSubmit,
    isOpen,
    onOpenChange,
  } = props;

  // Form and validation schema
  const formSchema = yup.object().shape({
    productTitle: yup.string().required('Product title is required'),
  });

  const methods = useForm<EditANewProductFormSchema>({
    resolver: yupResolver(formSchema),
    defaultValues: {
      productTitle: defaultValues.productTitle,
      brand: defaultValues.brand,
    },
    mode: 'all',
    delayError: 650,
  });

  const { control, handleSubmit, reset } = methods;

  const handleOnResetForm = React.useCallback(() => {
    reset({
      brand: defaultValues.brand,
      productTitle: defaultValues.productTitle,
    });
  }, [reset, defaultValues.brand, defaultValues.productTitle]);

  return (
    <FormDialog
      formName="edit-product-info"
      formMethods={methods}
      isOpen={isOpen}
      formFieldsClassName="gap-8"
      onOpenChange={onOpenChange}
      onSubmit={handleSubmit(onSubmit)}
      onResetForm={handleOnResetForm}
    >
      <FormDialog.Trigger>
        <Button
          level={ButtonLevel.Secondary}
          aria-label={triggerButtonText}
          iconStart={<Edit03Icon size={20} color={colors['gray-utility']['400']} />}
        >
          {triggerButtonText}
        </Button>
      </FormDialog.Trigger>
      <FormDialog.Title>Edit product info</FormDialog.Title>
      <FormDialog.Subtitle>Edit your product's title or brand to keep it up to date.</FormDialog.Subtitle>
      <FormDialog.FormFields>
        {/* Technically this is a text field, but it's not a form field so we need to use the TextField component */}
        <TextField
          label="Category"
          value={defaultValues.categoryName || ''}
          isRequired
          isDisabled
          description="Category cannot be changed"
          startIcon={<LockIcon size={20} />}
        />
        <FormTextField control={control} className="min-h-0" name="productTitle" label="Product title" isRequired />
        <FormTextField control={control} className="min-h-0" name="brand" label="Brand" />
      </FormDialog.FormFields>
      <FormDialog.SubmitButton>Save info</FormDialog.SubmitButton>
    </FormDialog>
  );
}

export { EditANewProductDialog };
export type { EditANewProductFormSchema, EditANewProductDialogProps };
